import React from 'react';
import { ArrowRight, ArrowUpRight, ShieldCheck, Building2, CheckCircle2 } from 'lucide-react';
import T from '@/components/landing/copy';
import { tt, Section, Eyebrow } from '@/components/landing/atoms';

// ─────────────────────────────────────────────────────────────────────────────
// HERO — premium real-estate-first hero
// Aesthetic: light/calm/architectural · NOT dark/cyber.
// Pattern: AppFolio + Buildium — bold operations headline, soft architectural
// backdrop, product dashboard preview on the right.
// ─────────────────────────────────────────────────────────────────────────────

// Premium architectural cityscape (KSA-leaning, dusk light, calm)
const CITYSCAPE_URL =
  'https://images.unsplash.com/photo-1518684079-3c830dcef090?auto=format&fit=crop&w=1800&q=80';

function DashboardPreview({ lang, isAr }) {
  // A miniature operational dashboard rendered as SVG/HTML — feels like a
  // real product screenshot floating in a glass frame. Pure CSS/SVG so it
  // is crisp at any zoom and reflects the actual dashboard aesthetic.
  return (
    <div className="reva-hero-preview" data-testid="reva-hero-preview" aria-hidden>
      {/* Top bar — like the actual app */}
      <div className="reva-hero-preview__bar">
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-[#FF6A55]" />
          <span className="w-1.5 h-1.5 rounded-full bg-[#FFB347]" />
          <span className="w-1.5 h-1.5 rounded-full bg-[#00A86B]" />
        </div>
        <span className="text-[10.5px] reva-mono text-[#71777E] tracking-[0.18em] uppercase">
          {isAr ? 'لوحة قيادة المحفظة' : 'Portfolio · Dashboard'}
        </span>
        <span
          className="text-[9px] reva-mono uppercase tracking-[0.14em] text-[#71777E] bg-[#F1F4F7] border border-[#E1E6EC] px-1.5 py-0.5 rounded-full"
          title={isAr ? 'بيانات توضيحيّة فقط' : 'Illustrative demo data only'}
        >
          {isAr ? 'عرض توضيحيّ' : 'preview'}
        </span>
      </div>

      {/* KPI strip */}
      <div className="reva-hero-preview__kpis">
        {[
          { k: isAr ? 'الإشغال'      : 'Occupancy',    v: '94.2%', d: '+1.8 pp', tone: '#00A86B' },
          { k: isAr ? 'التحصيل'      : 'Collections',  v: '98.1%', d: isAr ? '↑ فوق التوقّع' : '↑ forecast', tone: '#1FD18A' },
          { k: isAr ? 'صافي الدخل · شهريّ' : 'NOI · monthly', v: '2.8M', d: isAr ? '+11٪ سنويّ' : '+11% YoY', tone: '#1F66E5' },
          { k: isAr ? 'مهامّ مفتوحة'   : 'Open tasks',   v: '14',    d: isAr ? '٣ بانتظار الموافقة' : '3 pending', tone: '#D17A00' },
        ].map((m, i) => (
          <div key={i} className="reva-hero-preview__kpi">
            <div className="reva-hero-preview__kpi-k">{m.k}</div>
            <div className="reva-hero-preview__kpi-v">{m.v}</div>
            <div className="reva-hero-preview__kpi-d" style={{ color: m.tone }}>{m.d}</div>
          </div>
        ))}
      </div>

      {/* Body: chart + list */}
      <div className="reva-hero-preview__body">
        <div className="reva-hero-preview__chart">
          <div className="reva-hero-preview__chart-hd">
            <span className="text-[10.5px] reva-mono text-[#71777E] uppercase tracking-[0.16em]">
              {isAr ? 'الإيراد التشغيلي · ٣٠ يوم' : 'Operating revenue · 30d'}
            </span>
            <span className="text-[10.5px] reva-mono text-[#00A86B]">+8.4%</span>
          </div>
          <svg viewBox="0 0 320 110" className="w-full" preserveAspectRatio="none">
            <defs>
              <linearGradient id="hero-area" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%"   stopColor="#00A86B" stopOpacity="0.28" />
                <stop offset="100%" stopColor="#00A86B" stopOpacity="0" />
              </linearGradient>
            </defs>
            {/* Gridlines */}
            {[20, 50, 80].map(y => (
              <line key={y} x1="0" x2="320" y1={y} y2={y} stroke="#E6EAF0" strokeWidth="1" strokeDasharray="2 4" />
            ))}
            <path
              d="M0,80 L26,76 L52,72 L78,66 L104,68 L130,58 L156,54 L182,46 L208,42 L234,36 L260,32 L286,26 L320,22 L320,110 L0,110 Z"
              fill="url(#hero-area)"
            />
            <path
              d="M0,80 L26,76 L52,72 L78,66 L104,68 L130,58 L156,54 L182,46 L208,42 L234,36 L260,32 L286,26 L320,22"
              fill="none" stroke="#00A86B" strokeWidth="1.8" strokeLinecap="round"
            />
            <circle cx="320" cy="22" r="3" fill="#00A86B" />
          </svg>
        </div>
        <div className="reva-hero-preview__list">
          <div className="reva-hero-preview__list-hd">
            <span className="text-[10.5px] reva-mono text-[#71777E] uppercase tracking-[0.16em]">
              {isAr ? 'المهام التشغيلية' : 'Operational tasks'}
            </span>
          </div>
          {[
            { t: isAr ? 'تجديد عقد · وحدة ١٠١' : 'Renewal · Unit 101',     s: isAr ? 'بانتظار الموافقة' : 'pending', dot: '#1F66E5' },
            { t: isAr ? 'صيانة · مكيف' : 'Maintenance · HVAC',              s: isAr ? 'قيد التنفيذ' : 'in progress', dot: '#D17A00' },
            { t: isAr ? 'تحصيل · مستأجر' : 'Collections · Tenant 42',       s: isAr ? 'مكتمل' : 'completed', dot: '#00A86B' },
          ].map((it, i) => (
            <div key={i} className="reva-hero-preview__list-row">
              <span className="reva-hero-preview__list-dot" style={{ background: it.dot }} />
              <span className="text-[12px] text-[#0E1320] flex-1 truncate">{it.t}</span>
              <span className="text-[10px] reva-mono text-[#71777E] uppercase tracking-[0.14em]">{it.s}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Hero({ lang, onDemo, onTour }) {
  const isAr = lang === 'ar';

  return (
    <Section className="pt-12 md:pt-20 pb-16 md:pb-24">
      {/* Architectural backdrop — soft, cinematic, calm */}
      <div className="reva-hero-bg" aria-hidden>
        <div
          className="reva-hero-bg__photo"
          style={{ backgroundImage: `url(${CITYSCAPE_URL})` }}
        />
        <div className="reva-hero-bg__wash" />
        <div className="reva-hero-bg__grid" />
      </div>

      <div className="relative grid lg:grid-cols-12 gap-10 lg:gap-8 items-center">
        {/* LEFT — copy column */}
        <div className="lg:col-span-7">
          <div className="reva-rise" style={{ animationDelay: '0.04s' }}>
            <span className="reva-eyebrow-light" data-testid="reva-hero-eyebrow">
              <Building2 className="h-3 w-3 text-[color:var(--reva-signal)]" />
              {tt(T.hero.eyebrow, lang)}
            </span>
          </div>
          <h1
            className="reva-display-light mt-6 max-w-[18ch] text-[40px] sm:text-[56px] lg:text-[64px] leading-[1.04] tracking-[-0.02em] reva-rise"
            style={{ animationDelay: '0.16s' }}
            data-testid="reva-hero-title"
          >
            {tt(T.hero.title_a, lang)}{' '}
            <span className="reva-signal-text-light">{tt(T.hero.title_b, lang)}</span>{' '}
            {tt(T.hero.title_c, lang)}
          </h1>
          <p
            className="mt-6 max-w-2xl text-[16.5px] leading-[1.55] text-[#2A3340] reva-rise"
            style={{ animationDelay: '0.26s' }}
            data-testid="reva-hero-sub"
          >
            {tt(T.hero.sub, lang)}
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3 reva-rise" style={{ animationDelay: '0.36s' }}>
            <button onClick={onDemo} className="reva-btn-primary" data-testid="reva-hero-cta-demo">
              {tt(T.hero.ctaPrimary, lang)} <ArrowRight className="h-4 w-4" />
            </button>
            <button
              onClick={async () => {
                try {
                  const { api } = await import('@/utils/api');
                  const { data } = await api.sandboxCreate({ language: lang });
                  localStorage.setItem('token', data.access_token);
                  window.location.href = '/dashboard';
                } catch (e) {
                  // eslint-disable-next-line no-alert
                  alert((e?.response?.data?.detail) || 'Sandbox launch failed');
                }
              }}
              className="reva-btn-outline" data-testid="reva-hero-cta-sandbox"
            >
              {isAr ? 'جرّب ديمو معزولًا' : 'Try isolated sandbox'} <ArrowUpRight className="h-4 w-4" />
            </button>
          </div>

          {/* Trust strip — honest claims only */}
          <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-[11.5px] text-[#4A5263] reva-rise" style={{ animationDelay: '0.46s' }} data-testid="reva-hero-trust">
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5 text-[color:var(--reva-signal)]" strokeWidth={1.7} />
              {isAr ? 'عزل البيانات لكل عميل' : 'Tenant-isolated data'}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5 text-[color:var(--reva-signal)]" strokeWidth={1.7} />
              {isAr ? 'عربيّ وإنجليزيّ · ودعم RTL كامل' : 'Bilingual AR / EN · full RTL'}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5 text-[color:var(--reva-signal)]" strokeWidth={1.7} />
              {isAr ? 'استيراد سلس من Excel' : 'Painless Excel migration'}
            </span>
          </div>
        </div>

        {/* RIGHT — product preview */}
        <div className="lg:col-span-5 reva-rise" style={{ animationDelay: '0.34s' }}>
          <DashboardPreview lang={lang} isAr={isAr} />
        </div>
      </div>
    </Section>
  );
}
